import { Check, ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import styles from './ScopeFilterGroup.module.scss';

const ScopeChip = ({ label, options, selectedValues, onToggle, onClear }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selectedOptions = options.filter((option) => selectedValues.includes(option.value));
  const anchor = selectedOptions[0];

  return (
    <div ref={rootRef} className={styles.chipRoot}>
      <button
        type="button"
        className={styles.chipTrigger}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label} filter: ${anchor ? selectedOptions.map((o) => o.label).join(', ') : 'All'}`}
      >
        <span className={styles.chipLabel}>{label}</span>
        {anchor ? (
          <>
            <span className={styles.chipValue}>{anchor.label}</span>
            {selectedOptions.length > 1 && (
              <span className={styles.chipBadge}>+{selectedOptions.length - 1}</span>
            )}
          </>
        ) : (
          <span className={styles.chipAll}>All</span>
        )}
        <ChevronDown size={14} strokeWidth={2} className={styles.chevron} aria-hidden="true" />
      </button>

      {open && (
        <div className={styles.dropdown} role="listbox" aria-multiselectable="true">
          <button
            type="button"
            role="option"
            aria-selected={selectedValues.length === 0}
            className={styles.option}
            onClick={onClear}
          >
            <Check
              size={12}
              strokeWidth={2.5}
              className={styles.optionCheck}
              style={{ opacity: selectedValues.length === 0 ? 1 : 0 }}
              aria-hidden="true"
            />
            <span className={styles.optionLabel}>All</span>
          </button>
          <div className={styles.optionDivider} />
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={styles.option}
                onClick={() => onToggle(option.value)}
              >
                <Check
                  size={12}
                  strokeWidth={2.5}
                  className={styles.optionCheck}
                  style={{ opacity: isSelected ? 1 : 0 }}
                  aria-hidden="true"
                />
                <span className={styles.optionLabel}>{option.label}</span>
                {option.tone && option.tone !== 'default' && (
                  <span className={styles.optionTone} data-tone={option.tone} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

ScopeChip.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      tone: PropTypes.string,
    }),
  ).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default ScopeChip;
